// public/js/features/pois/settings-tenant.js
// Settings → Tenant Settings: toggle which public POI type packages are active
// for the tenant, and manage tenant-own (and, for admins, public) POI types.
import { loadMyTenants, loadPoiData } from './api.js';
import { primePoiData } from './poi-data.js';
import { updateTenant } from '../../modules/tenants.js';
import { can, getCurrentUser } from '../../modules/rbac.js';
import { scopeTenantParam } from '../../modules/tenant-scope.js';
import { scopePromptHtml } from '../../ui/tenant-scope-widget.js';

let selectedTenantId = null;

export async function renderTenantSettings(container) {
  const groups = getCurrentUser().groups || [];
  if (!can(groups, 'managePoiTypes')) {
    container.innerHTML = `<div class="alert alert-danger">Access denied.</div>`;
    return;
  }

  container.innerHTML = `<div class="text-muted py-3"><span class="spinner-border spinner-border-sm me-2"></span>Loading…</div>`;

  let my, data;
  try {
    [my, data] = await Promise.all([loadMyTenants(), loadPoiData()]);
  } catch (e) {
    container.innerHTML = `<div class="alert alert-danger">Failed to load tenant settings.</div>`;
    return;
  }
  // Fresh from the backend - keep the shared cache in step (see settings-pois).
  primePoiData(data.types, data.pois);

  const tenants = my.tenants || [];
  const canPublic = can(groups, 'managePublicPois');
  // Global tenant scope (hard-coupled): the sidebar selector is the ONLY
  // tenant choice. Under "All tenants" the per-tenant part becomes a prompt;
  // the public-types admin card below stays (it is scope-free).
  const scopeTid = scopeTenantParam();
  const scopePinned = scopeTid && scopeTid !== 'unassigned' && tenants.some(t => t.tenantId === scopeTid);
  const scopePrompted = tenants.length > 1 && !scopePinned;
  selectedTenantId = scopePinned ? scopeTid : (tenants[0]?.tenantId || null);
  const selected = scopePrompted ? null : (tenants.find(t => t.tenantId === selectedTenantId) || null);
  const disabledSet = new Set(selected?.disabledPublicTypeIds || []);

  const publicTypes = data.types.filter(t => t.scope === 'public');
  const tenantTypes = data.types.filter(t => t.scope === selectedTenantId);

  const reload = () => renderTenantSettings(container);

  container.innerHTML = `
    ${scopePrompted ? scopePromptHtml('this tenant&#39;s POI packages and types') : selected ? `
    <div class="card shadow-sm mb-4">
      <div class="card-header bg-white fw-semibold"><i class="fa-solid fa-toggle-on me-2"></i>Public POI packages</div>
      <div class="card-body">
        <p class="text-muted small mb-3">Turn public POI types on or off for <strong class="tenant-name">${esc(selected.name || selectedTenantId)}</strong>. Disabled packages are hidden from the map for this tenant.</p>
        ${publicTypes.length === 0 ? `<div class="text-muted fst-italic">No public POI types available.</div>` : `
        <div class="list-group">
          ${publicTypes.map(t => `
            <div class="list-group-item d-flex align-items-center justify-content-between">
              <span class="d-flex align-items-center gap-2">${typeSwatch(t)}<span>${esc(t.name)}</span></span>
              <div class="form-check form-switch m-0">
                <input class="form-check-input ts-pkg-toggle" type="checkbox" data-type-id="${t.poiId}" ${disabledSet.has(t.poiId) ? '' : 'checked'}>
              </div>
            </div>`).join('')}
        </div>`}
      </div>
    </div>

    <div class="card shadow-sm mb-4">
      <div class="card-header bg-white d-flex justify-content-between align-items-center">
        <span class="fw-semibold"><i class="fa-solid fa-tags me-2"></i>Tenant POI types</span>
        <button id="ts-new-tenant-type" class="btn btn-sm btn-success"><i class="fa-solid fa-plus me-1"></i>New type</button>
      </div>
      <div class="card-body">
        ${typeList(tenantTypes, 'No tenant-specific types yet.')}
      </div>
    </div>` : `<div class="alert alert-info">You are not a member of any tenant.</div>`}

    ${canPublic ? `
    <div class="card shadow-sm mb-2 border-info">
      <div class="card-header bg-white d-flex justify-content-between align-items-center">
        <span class="fw-semibold"><i class="fa-solid fa-globe me-2"></i>Public POI types (admin)</span>
        <button id="ts-new-public-type" class="btn btn-sm btn-outline-info"><i class="fa-solid fa-plus me-1"></i>New public type</button>
      </div>
      <div class="card-body">
        ${typeList(publicTypes, 'No public types yet.')}
      </div>
    </div>` : ''}
  `;

  // Tenant selector
  container.querySelector('#ts-tenant')?.addEventListener('change', (e) => {
    selectedTenantId = e.target.value;
    reload();
  });

  // Package toggles → persist disabledPublicTypeIds on the tenant.
  container.querySelectorAll('.ts-pkg-toggle').forEach(cb => {
    cb.addEventListener('change', async () => {
      const id = cb.dataset.typeId;
      if (cb.checked) disabledSet.delete(id); else disabledSet.add(id);
      try {
        await updateTenant(selectedTenantId, { disabledPublicTypeIds: [...disabledSet] });
        window.showToast('Saved', 'success');
        await window.poiRefreshData?.();
      } catch (err) {
        window.showToast('Failed to save', 'danger');
        cb.checked = !cb.checked; // revert
        if (cb.checked) disabledSet.delete(id); else disabledSet.add(id);
      }
    });
  });

  // Type create/edit
  container.querySelector('#ts-new-tenant-type')?.addEventListener('click', () => {
    window.openPoiTypeModal?.({ mode: 'create', scope: selectedTenantId, onSaved: reload });
  });
  container.querySelector('#ts-new-public-type')?.addEventListener('click', () => {
    window.openPoiTypeModal?.({ mode: 'create', scope: 'public', onSaved: reload });
  });
  container.querySelectorAll('.ts-type-row').forEach(row => {
    row.addEventListener('click', () => {
      const all = [...publicTypes, ...tenantTypes];
      const type = all.find(t => t.poiId === row.dataset.typeId);
      if (type) window.openPoiTypeModal?.({ mode: 'edit', type, onSaved: reload });
    });
  });
}

function typeList(types, emptyMsg) {
  if (types.length === 0) return `<div class="text-muted fst-italic">${emptyMsg}</div>`;
  return `<div class="list-group">
    ${types.map(t => `
      <button type="button" class="list-group-item list-group-item-action d-flex align-items-center gap-2 ts-type-row" data-type-id="${t.poiId}">
        ${typeSwatch(t)}<span>${esc(t.name)}</span>
        ${t.shortName ? `<span class="badge bg-light text-dark ms-1">${esc(t.shortName)}</span>` : ''}
        <i class="fa-solid fa-pen ms-auto text-muted"></i>
      </button>`).join('')}
  </div>`;
}

function typeSwatch(t) {
  return `<span style="display:inline-flex;width:22px;height:22px;border-radius:5px;background:${t.color || '#0d6efd'};color:#fff;align-items:center;justify-content:center;font-size:11px;"><i class="${t.icon || 'fa-solid fa-location-dot'}"></i></span>`;
}

function esc(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
